import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Mail,
  Phone,
  Image as ImageIcon,
  Trash2,
  GitCompare,
  Instagram,
  Music2,
  Youtube,
  Facebook,
  MessageCircle,
  MapPin,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import api from '../../lib/api';
import { fromSlug, kolUrlSegment } from '../../lib/slug';
import { formatPercentOneDecimal } from '../../lib/utils';
import { kolPlatformFollowers, kolTotalFollowers } from '../../lib/kolFollowers';
import {
  SocialHandleLink,
  instagramProfileUrl,
  tiktokProfileUrl,
  youtubeProfileUrl,
  facebookProfileUrl,
} from '../../lib/socialProfileLinks';
import { KOLProfile, type KolGender } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import FileUploader from '../../components/ui/FileUploader';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import RateCardManager, { normalizeRatePlatform } from '../../components/RateCardManager';

type MobileDetailSection = 'portfolio' | 'rates' | 'availability';

type MobilePlatformRow = {
  key: string;
  label: string;
  icon: LucideIcon;
  display: string;
  href: string | null;
  followers: number;
  engagement?: number | string | null;
};

function clampPercentage(value: unknown): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 0;
  return Math.max(0, Math.min(100, numberValue));
}

export default function KOLDetailPage() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const [kol, setKol] = useState<KOLProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [compareKolId, setCompareKolId] = useState('');
  const [rosterKols, setRosterKols] = useState<KOLProfile[]>([]);
  const [compareKol, setCompareKol] = useState<KOLProfile | null>(null);
  const [expandedMobileSections, setExpandedMobileSections] = useState<Set<MobileDetailSection>>(
    new Set()
  );

  useEffect(() => {
    loadKOL();
  }, [id]);

  useEffect(() => {
    if (!kol?.agencyOrgId) return;
    (async () => {
      try {
        const res = await api.get('/kols', { params: { agencyOrgId: kol.agencyOrgId } });
        setRosterKols((res.data || []).filter((k: KOLProfile) => k.id !== kol.id));
      } catch {
        setRosterKols([]);
      }
    })();
  }, [kol?.agencyOrgId, kol?.id]);

  useEffect(() => {
    if (!compareKolId) {
      setCompareKol(null);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/kols/${compareKolId}`);
        setCompareKol(res.data);
      } catch {
        setCompareKol(null);
      }
    })();
  }, [compareKolId]);

  const loadKOL = async () => {
    try {
      const response = await api.get(`/kols/${id}`);
      setKol(response.data);
    } catch (error) {
      console.error('Failed to load KOL:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    if (!kol) return;
    navigate(`/agency/kols/${kolUrlSegment(kol)}/edit`);
  };

  const toggleMobileSection = (section: MobileDetailSection) => {
    setExpandedMobileSections((current) => {
      const next = new Set(current);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-glass-text-2">
        Loading...
      </div>
    );
  }

  if (!kol) {
    return <div className="text-center py-12 text-glass-text-3">KOL not found</div>;
  }

  const platforms = kol.platforms as Record<string, any> | undefined;
  const audience = kol.audience as any;

  const kolGenderLabel = (g: KolGender | null | undefined): string => {
    if (!g) return '';
    const map: Record<KolGender, string> = {
      FEMALE: 'Female',
      MALE: 'Male',
      NON_BINARY: 'Non-binary',
      OTHER: 'Other',
      PREFER_NOT_TO_SAY: 'Prefer not to say',
    };
    return map[g] ?? g;
  };

  const hasAnySocialPlatform =
    Boolean(platforms && Object.keys(platforms).length > 0) ||
    kol.instagramHandle ||
    kol.instagramProfileUrl ||
    kol.instagramFollowers != null ||
    kol.tiktokHandle ||
    kol.tiktokProfileUrl ||
    kol.tiktokFollowers != null ||
    kol.youtubeChannel ||
    kol.youtubeChannelUrl ||
    kol.youtubeFollowers != null ||
    kol.facebookHandle ||
    kol.facebookProfileUrl ||
    kol.facebookFollowers != null ||
    kol.wechatId ||
    kol.wechatProfileUrl ||
    kol.wechatFollowers != null;
  const malePct = audience?.demographics?.male ?? audience?.sexPercentage?.male;
  const femalePct = audience?.demographics?.female ?? audience?.sexPercentage?.female;
  const dem = audience?.demographics;
  const hasAgeBreakdown =
    dem != null &&
    typeof dem === 'object' &&
    (dem.age18_24 !== undefined || dem.age25_34 !== undefined || dem.age35_plus !== undefined);
  const ageGroupLabel = audience?.ageGroup;
  const hasSexData = malePct !== undefined || femalePct !== undefined;
  const hasSexOrAgeContent = hasSexData || hasAgeBreakdown || Boolean(ageGroupLabel);
  const ethnic = audience?.ethnicMix;
  const hasEthnicMix =
    ethnic != null &&
    typeof ethnic === 'object' &&
    (ethnic.malay !== undefined ||
      ethnic.chinese !== undefined ||
      ethnic.indian !== undefined ||
      ethnic.others !== undefined);
  const majorityRace = audience?.majorityRace;

  const igHandleRaw = kol.instagramHandle || platforms?.instagram?.handle;
  const igHref =
    kol.instagramProfileUrl ||
    (platforms?.instagram as { url?: string } | undefined)?.url ||
    instagramProfileUrl(igHandleRaw);
  const igDisplay = (() => {
    if (igHandleRaw == null || String(igHandleRaw).trim() === '—') return '—';
    return `@${String(igHandleRaw).replace(/^@+/, '')}`;
  })();
  const ttHandleRaw = kol.tiktokHandle || platforms?.tiktok?.handle;
  const ttHref =
    kol.tiktokProfileUrl ||
    (platforms?.tiktok as { url?: string } | undefined)?.url ||
    tiktokProfileUrl(ttHandleRaw);
  const ttDisplay = (() => {
    if (ttHandleRaw == null || String(ttHandleRaw).trim() === '—') return '—';
    return `@${String(ttHandleRaw).replace(/^@+/, '')}`;
  })();
  const ytChannelRaw = kol.youtubeChannel || platforms?.youtube?.handle;
  const ytHref =
    kol.youtubeChannelUrl ||
    (platforms?.youtube as { url?: string } | undefined)?.url ||
    youtubeProfileUrl(ytChannelRaw);
  const fbHandleRaw = kol.facebookHandle || platforms?.facebook?.handle;
  const fbHref =
    kol.facebookProfileUrl ||
    (platforms?.facebook as { url?: string } | undefined)?.url ||
    facebookProfileUrl(fbHandleRaw);
  const wxHref =
    kol.wechatProfileUrl || (platforms?.wechat as { url?: string } | undefined)?.url || null;
  const fbDisplay = (() => {
    if (fbHandleRaw == null || String(fbHandleRaw).trim() === '—') return '—';
    return `@${String(fbHandleRaw).replace(/^@+/, '')}`;
  })();

  const mobilePlatformRows: MobilePlatformRow[] = [];
  if (platforms?.instagram || kol.instagramHandle || kol.instagramProfileUrl || kol.instagramFollowers != null) {
    mobilePlatformRows.push({
      key: 'instagram',
      label: 'Instagram',
      icon: Instagram,
      display: igDisplay,
      href: igHref || null,
      followers: kolPlatformFollowers(kol, 'instagram'),
      engagement: platforms?.instagram?.engagement ?? platforms?.instagram?.engagementRate,
    });
  }
  if (platforms?.tiktok || kol.tiktokHandle || kol.tiktokProfileUrl || kol.tiktokFollowers != null) {
    mobilePlatformRows.push({
      key: 'tiktok',
      label: 'TikTok',
      icon: Music2,
      display: ttDisplay,
      href: ttHref || null,
      followers: kolPlatformFollowers(kol, 'tiktok'),
      engagement: platforms?.tiktok?.engagement ?? platforms?.tiktok?.engagementRate,
    });
  }
  if (platforms?.youtube || kol.youtubeChannel || kol.youtubeChannelUrl || kol.youtubeFollowers != null) {
    mobilePlatformRows.push({
      key: 'youtube',
      label: 'YouTube',
      icon: Youtube,
      display:
        ytChannelRaw && String(ytChannelRaw).trim() !== '—' ? String(ytChannelRaw).trim() : '—',
      href: ytHref || null,
      followers: kolPlatformFollowers(kol, 'youtube'),
      engagement: platforms?.youtube?.engagement ?? platforms?.youtube?.engagementRate,
    });
  }
  if (platforms?.facebook || kol.facebookHandle || kol.facebookProfileUrl || kol.facebookFollowers != null) {
    mobilePlatformRows.push({
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      display: fbDisplay,
      href: fbHref || null,
      followers: kolPlatformFollowers(kol, 'facebook'),
      engagement: platforms?.facebook?.engagement ?? platforms?.facebook?.engagementRate,
    });
  }
  if (platforms?.wechat || kol.wechatId || kol.wechatProfileUrl || kol.wechatFollowers != null) {
    mobilePlatformRows.push({
      key: 'wechat',
      label: 'WeChat',
      icon: MessageCircle,
      display: kol.wechatId || platforms?.wechat?.handle || '—',
      href: wxHref,
      followers: kolPlatformFollowers(kol, 'wechat'),
    });
  }

  const rateCards = (kol.rateCards as any[]) || [];
  const firstPostRate = rateCards.find((rate) => rate.postRate != null)?.postRate;
  const mobileRateSummary =
    firstPostRate != null
      ? `RM ${Number(firstPostRate).toLocaleString()} post`
      : `${rateCards.length.toLocaleString()} ${rateCards.length === 1 ? 'rate' : 'rates'}`;
  const availability = ((kol as any).availability || []) as any[];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Mobile profile bar and summary */}
      <div className="space-y-3 md:hidden">
        <div className="flex h-12 items-center gap-1 border-b border-glass bg-glass-1 backdrop-blur-[28px]">
          <button
            type="button"
            onClick={() => navigate('/agency/kols')}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
            aria-label="Back to KOL database"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold text-glass-text">{kol.name}</p>
            <p className="text-[11px] text-glass-text-3">Creator profile</p>
          </div>
          <button
            type="button"
            onClick={handleEditClick}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
            aria-label={`Edit ${kol.name}`}
            title="Edit profile"
          >
            <Edit className="h-5 w-5" />
          </button>
        </div>

        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            {kol.profileImageUrl ? (
              <img
                src={resolveMediaUrl(kol.profileImageUrl)}
                alt={kol.name}
                className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-glass"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent2/15 text-lg font-semibold text-accentText">
                {kol.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-display text-xl font-extrabold tracking-tightest text-glass-text">
                {kol.name}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-glass-text-3">
                {kol.gender && <span>{kolGenderLabel(kol.gender)}</span>}
                {kol.locationCity && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {kol.locationCity}
                  </span>
                )}
              </div>
            </div>
          </div>
          {(kol.email || kol.phone) && (
            <div className="mt-3 grid grid-cols-1 gap-1 border-t border-glass pt-2">
              {kol.email && (
                <a
                  href={`mailto:${kol.email}`}
                  className="flex min-h-11 min-w-0 items-center gap-2 rounded-xl px-2 text-sm text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                >
                  <Mail className="h-4 w-4 shrink-0 text-accentText" />
                  <span className="truncate">{kol.email}</span>
                </a>
              )}
              {kol.phone && (
                <a
                  href={`tel:${kol.phone}`}
                  className="flex min-h-11 min-w-0 items-center gap-2 rounded-xl px-2 text-sm text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                >
                  <Phone className="h-4 w-4 shrink-0 text-accentText" />
                  <span className="truncate">{kol.phone}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden items-center justify-between md:flex">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/agency/kols')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-4">
            {kol.profileImageUrl ? (
              <img
                src={resolveMediaUrl(kol.profileImageUrl)}
                alt={kol.name}
                className="w-16 h-16 rounded-full object-cover ring-1 ring-glass"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-accent2/15 flex items-center justify-center">
                <span className="text-accent2 text-xl font-semibold">{kol.name[0]}</span>
              </div>
            )}
            <div>
              <h1 className="page-title">{kol.name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-glass-text-2">
                {kol.gender && (
                  <span>
                    <span className="text-glass-text-3">Gender: </span>
                    {kolGenderLabel(kol.gender)}
                  </span>
                )}
                {kol.email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {kol.email}
                  </div>
                )}
                {kol.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {kol.phone}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={handleEditClick}>
          <Edit className="w-4 h-4 mr-2" />
          Edit Profile
        </Button>
      </div>

      {/* Tags */}
      {kol.tags && kol.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {kol.tags.map((t) => (
            <Badge key={t.tag.id} variant="secondary">{t.tag.name}</Badge>
          ))}
        </div>
      )}

      {/* Total reach (uses DB columns + JSON like brand view) */}
      <div className="hidden max-w-lg grid-cols-1 gap-4 md:grid md:grid-cols-2">
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-semibold text-glass-text tracking-tightest">
            {kolTotalFollowers(kol).toLocaleString()}
          </div>
          <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em] mt-1">
            Total followers (all platforms)
          </div>
        </div>
      </div>

      {/* Mobile social reach strip */}
      <Card className="overflow-hidden md:hidden">
        <div className="flex items-end justify-between gap-3 border-b border-glass px-3.5 py-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.05em] text-glass-text-3">
              Total followers
            </p>
            <p className="stat-display mt-1 text-2xl text-glass-text">
              {kolTotalFollowers(kol).toLocaleString()}
            </p>
          </div>
          <span className="text-xs text-glass-text-3">
            {mobilePlatformRows.length} {mobilePlatformRows.length === 1 ? 'platform' : 'platforms'}
          </span>
        </div>
        {mobilePlatformRows.length > 0 ? (
          <div className="divide-y divide-glass">
            {mobilePlatformRows.map((platform) => {
              const Icon = platform.icon;
              return (
                <div key={platform.key} className="flex min-h-11 items-center gap-2.5 px-3.5 py-1.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent2/10 text-accentText">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-glass-text">{platform.label}</p>
                    {platform.href ? (
                      <SocialHandleLink
                        href={platform.href}
                        className="block truncate text-[11px] text-glass-text-3 underline-offset-2 hover:text-accent2 hover:underline dark:hover:text-violet-400"
                      >
                        {platform.display}
                      </SocialHandleLink>
                    ) : (
                      <p className="truncate text-[11px] text-glass-text-3">{platform.display}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="stat-display text-sm text-glass-text">
                      {platform.followers.toLocaleString()}
                    </p>
                    <p className="mt-0.5 text-[10px] text-glass-text-3">
                      {platform.engagement != null
                        ? `${formatPercentOneDecimal(platform.engagement)} eng.`
                        : 'followers'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="px-3.5 py-5 text-sm text-glass-text-3">No social platforms added yet.</p>
        )}
      </Card>

      {/* Social platforms — same data sources as brand KOL detail */}
      <div className="hidden grid-cols-1 gap-6 md:grid md:grid-cols-2">
        {(platforms?.instagram || kol.instagramHandle || kol.instagramProfileUrl || kol.instagramFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">📷</span>
                Instagram
                {kol.instagramConnection?.connected && (
                  /* A creator-authorised connection is the only source of Instagram numbers that
                     comes from Meta rather than from a parsed page, so it is worth saying which
                     one an agency is looking at before they price against it. */
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--pos))]/15 px-2 py-0.5 text-[0.7rem] font-medium text-[rgb(var(--pos))]"
                    title="This creator connected their own Instagram account, so these figures come from Meta directly."
                  >
                    Creator-connected
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {kol.instagramConnection?.connected && kol.instagramConnection.lastSyncedAt && (
                  <div className="flex justify-between">
                    <span className="text-glass-text-2">Last synced</span>
                    <span className="text-glass-text-3">
                      {new Date(kol.instagramConnection.lastSyncedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Handle</span>
                  <SocialHandleLink
                    href={igHref}
                    className="font-medium text-glass-text hover:text-accent2 underline-offset-2 hover:underline"
                  >
                    {igDisplay}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Followers</span>
                  <span className="font-medium text-glass-text">{kolPlatformFollowers(kol, 'instagram').toLocaleString()}</span>
                </div>
                {(platforms?.instagram?.engagement ?? platforms?.instagram?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-glass-text-2">Engagement</span>
                    <span className="font-medium text-accent2">
                      {formatPercentOneDecimal(
                        platforms!.instagram.engagement ?? platforms!.instagram.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.tiktok || kol.tiktokHandle || kol.tiktokProfileUrl || kol.tiktokFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🎵</span>
                TikTok
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Handle</span>
                  <SocialHandleLink
                    href={ttHref}
                    className="font-medium text-glass-text hover:text-accent2 underline-offset-2 hover:underline"
                  >
                    {ttDisplay}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Followers</span>
                  <span className="font-medium text-glass-text">{kolPlatformFollowers(kol, 'tiktok').toLocaleString()}</span>
                </div>
                {(platforms?.tiktok?.engagement ?? platforms?.tiktok?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-glass-text-2">Engagement</span>
                    <span className="font-medium text-accent2">
                      {formatPercentOneDecimal(
                        platforms!.tiktok.engagement ?? platforms!.tiktok.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.youtube || kol.youtubeChannel || kol.youtubeChannelUrl || kol.youtubeFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">▶️</span>
                YouTube
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Channel</span>
                  <SocialHandleLink
                    href={ytHref}
                    className="font-medium text-glass-text hover:text-accent2 underline-offset-2 hover:underline break-all text-right max-w-[min(100%,12rem)]"
                  >
                    {ytChannelRaw && String(ytChannelRaw).trim() !== '—' ? String(ytChannelRaw).trim() : '—'}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Subscribers</span>
                  <span className="font-medium text-glass-text">{kolPlatformFollowers(kol, 'youtube').toLocaleString()}</span>
                </div>
                {(platforms?.youtube?.engagement ?? platforms?.youtube?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-glass-text-2">Engagement</span>
                    <span className="font-medium text-accent2">
                      {formatPercentOneDecimal(
                        platforms!.youtube.engagement ?? platforms!.youtube.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.facebook || kol.facebookHandle || kol.facebookProfileUrl || kol.facebookFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">👥</span>
                Facebook
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Handle</span>
                  <SocialHandleLink
                    href={fbHref}
                    className="font-medium text-glass-text hover:text-accent2 underline-offset-2 hover:underline"
                  >
                    {fbDisplay}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Followers</span>
                  <span className="font-medium text-glass-text">{kolPlatformFollowers(kol, 'facebook').toLocaleString()}</span>
                </div>
                {(platforms?.facebook?.engagement ?? platforms?.facebook?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-glass-text-2">Engagement</span>
                    <span className="font-medium text-accent2">
                      {formatPercentOneDecimal(
                        platforms!.facebook.engagement ?? platforms!.facebook.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.wechat || kol.wechatId || kol.wechatProfileUrl || kol.wechatFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">💬</span>
                WeChat
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-glass-text-2">ID</span>
                  <span className="font-medium text-glass-text">{kol.wechatId || platforms?.wechat?.handle || '—'}</span>
                </div>
                {wxHref ? (
                  <div className="flex justify-between">
                    <span className="text-glass-text-2">Link</span>
                    <SocialHandleLink
                      href={wxHref}
                      className="font-medium text-glass-text hover:text-accent2 underline-offset-2 hover:underline break-all text-right max-w-[min(100%,12rem)]"
                    >
                      Open
                    </SocialHandleLink>
                  </div>
                ) : null}
                <div className="flex justify-between">
                  <span className="text-glass-text-2">Followers</span>
                  <span className="font-medium text-glass-text">{kolPlatformFollowers(kol, 'wechat').toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {!hasAnySocialPlatform && (
        <p className="hidden text-sm text-glass-text-3 md:block">No social platforms added yet.</p>
      )}

      {/* Mobile combined audience card */}
      <Card className="md:hidden">
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">Audience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-4 pt-0">
          {hasSexData && (
            <div>
              <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.05em] text-glass-text-3">
                Sex
              </p>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-glass-3" aria-label="Audience sex distribution">
                {malePct !== undefined && (
                  <span
                    className="h-full bg-cyan-500 dark:bg-cyan-300"
                    style={{ width: `${clampPercentage(malePct)}%` }}
                    title={`Male ${malePct}%`}
                  />
                )}
                {femalePct !== undefined && (
                  <span
                    className="h-full bg-rose-500 dark:bg-rose-300"
                    style={{ width: `${clampPercentage(femalePct)}%` }}
                    title={`Female ${femalePct}%`}
                  />
                )}
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[11px] text-glass-text-3">
                <span>{malePct !== undefined ? `Male ${malePct}%` : 'Male —'}</span>
                <span>{femalePct !== undefined ? `Female ${femalePct}%` : 'Female —'}</span>
              </div>
            </div>
          )}

          {(hasAgeBreakdown || ageGroupLabel) && (
            <div>
              <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.05em] text-glass-text-3">
                Age groups
              </p>
              {hasAgeBreakdown && dem ? (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: '18–24', value: dem.age18_24 },
                    { label: '25–34', value: dem.age25_34 },
                    { label: '35+', value: dem.age35_plus },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl border border-glass bg-glass-2 px-2 py-2 text-center">
                      <p className="stat-display text-sm text-glass-text">
                        {item.value !== undefined ? `${item.value}%` : '—'}
                      </p>
                      <p className="mt-1 text-[10px] text-glass-text-3">{item.label}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-glass bg-glass-2 px-3 py-2 text-sm font-semibold text-glass-text">
                  {ageGroupLabel}
                </div>
              )}
            </div>
          )}

          {(hasEthnicMix || majorityRace) && (
            <div>
              <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.05em] text-glass-text-3">
                Ethnic mix
              </p>
              {hasEthnicMix ? (
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Malay', value: ethnic?.malay },
                    { label: 'Chinese', value: ethnic?.chinese },
                    { label: 'Indian', value: ethnic?.indian },
                    { label: 'Others', value: ethnic?.others },
                  ]
                    .filter((item) => item.value !== undefined)
                    .map((item) => (
                      <span
                        key={item.label}
                        className="inline-flex min-h-8 items-center rounded-full border border-glass bg-glass-3 px-3 text-xs font-medium text-glass-text"
                      >
                        {item.label} {item.value}%
                      </span>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-glass-text-2">Majority: {majorityRace}</p>
              )}
            </div>
          )}

          {!hasSexOrAgeContent && !hasEthnicMix && !majorityRace && (
            <p className="text-sm text-glass-text-3">No audience data</p>
          )}
        </CardContent>
      </Card>

      <div className="hidden grid-cols-1 gap-6 md:grid lg:grid-cols-2">
        {/* Audience Demographics - Sex & Age */}
        <Card>
          <CardHeader>
            <CardTitle>Sex & Age Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {hasSexOrAgeContent ? (
              <div className="space-y-4">
                {hasSexData && (
                  <div>
                    <h4 className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-2">
                      Sex
                    </h4>
                    <div className="space-y-2">
                      {malePct !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-glass-text-2">Male</span>
                          <span className="font-semibold text-cyan-500 dark:text-cyan-300">
                            {malePct}%
                          </span>
                        </div>
                      )}
                      {femalePct !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-glass-text-2">Female</span>
                          <span className="font-semibold text-rose-500 dark:text-rose-300">
                            {femalePct}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(hasAgeBreakdown || ageGroupLabel) && (
                  <div className={hasSexData ? 'border-t border-glass pt-3' : ''}>
                    <h4 className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-2">
                      Age Groups
                    </h4>
                    <div className="space-y-2">
                      {hasAgeBreakdown && dem && (
                        <>
                          {dem.age18_24 !== undefined && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-glass-text-2">18-24</span>
                              <span className="font-semibold text-emerald-500 dark:text-emerald-300">
                                {dem.age18_24}%
                              </span>
                            </div>
                          )}
                          {dem.age25_34 !== undefined && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-glass-text-2">25-34</span>
                              <span className="font-semibold text-emerald-500 dark:text-emerald-300">
                                {dem.age25_34}%
                              </span>
                            </div>
                          )}
                          {dem.age35_plus !== undefined && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-glass-text-2">35+</span>
                              <span className="font-semibold text-emerald-500 dark:text-emerald-300">
                                {dem.age35_plus}%
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {!hasAgeBreakdown && ageGroupLabel && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-glass-text-2">Primary age group</span>
                          <span className="font-semibold text-emerald-500 dark:text-emerald-300">
                            {ageGroupLabel}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-glass-text-3">No demographic data</p>
            )}
          </CardContent>
        </Card>

        {/* Ethnic Mix */}
        <Card>
          <CardHeader>
            <CardTitle>Ethnic Mix</CardTitle>
          </CardHeader>
          <CardContent>
            {hasEthnicMix ? (
              <div className="space-y-3">
                {ethnic?.malay !== undefined && (
                  <div className="flex items-center justify-between p-2 bg-purple-500/10 rounded-lg">
                    <span className="text-sm font-medium text-glass-text">Malay</span>
                    <span className="font-bold text-purple-500 dark:text-purple-300">
                      {ethnic.malay}%
                    </span>
                  </div>
                )}
                {ethnic?.chinese !== undefined && (
                  <div className="flex items-center justify-between p-2 bg-rose-500/10 rounded-lg">
                    <span className="text-sm font-medium text-glass-text">Chinese</span>
                    <span className="font-bold text-rose-500 dark:text-rose-300">
                      {ethnic.chinese}%
                    </span>
                  </div>
                )}
                {ethnic?.indian !== undefined && (
                  <div className="flex items-center justify-between p-2 bg-amber-500/10 rounded-lg">
                    <span className="text-sm font-medium text-glass-text">Indian</span>
                    <span className="font-bold text-amber-500 dark:text-amber-300">
                      {ethnic.indian}%
                    </span>
                  </div>
                )}
                {ethnic?.others !== undefined && (
                  <div className="flex items-center justify-between p-2 bg-glass-3 rounded-lg">
                    <span className="text-sm font-medium text-glass-text">Others</span>
                    <span className="font-bold text-glass-text-2">{ethnic.others}%</span>
                  </div>
                )}
              </div>
            ) : majorityRace ? (
              <p className="text-sm text-glass-text">
                <span className="text-glass-text-3">Majority: </span>
                {majorityRace}
              </p>
            ) : (
              <p className="text-sm text-glass-text-3">No ethnic data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {kol.notes && (
        <Card>
          <CardHeader className="p-4 pb-3 md:p-6 md:pb-6">
            <CardTitle className="text-base md:text-2xl">Notes</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm md:p-6 md:pt-0 md:text-base">
            <p className="text-glass-text-2 whitespace-pre-wrap">{kol.notes}</p>
          </CardContent>
        </Card>
      )}

      {(kol.bio || kol.locationCity || kol.ethnicity) && (
        <Card>
          <CardHeader className="p-4 pb-3 md:p-6 md:pb-6">
            <CardTitle className="text-base md:text-2xl">Profile details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-4 pt-0 text-sm text-glass-text-2 md:p-6 md:pt-0">
            {kol.bio && <p className="whitespace-pre-wrap">{kol.bio}</p>}
            <div className="flex flex-wrap gap-4">
              {kol.locationCity && <span>Location: {kol.locationCity}</span>}
              {kol.ethnicity && <span>Ethnicity: {kol.ethnicity}</span>}
            </div>
            {(kol.avgPostRate != null || kol.avgVideoRate != null) && (
              <div className="text-xs text-glass-text-3">
                Guide rates (RM): post {kol.avgPostRate ?? '—'} · video {kol.avgVideoRate ?? '—'} · story {kol.avgStoryRate ?? '—'}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="hidden md:flex">
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Portfolio
          </CardTitle>
        </CardHeader>
        <button
          type="button"
          onClick={() => toggleMobileSection('portfolio')}
          className="flex min-h-14 w-full items-center gap-3 px-4 text-left transition-colors hover:bg-glass-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50 md:hidden"
          aria-expanded={expandedMobileSections.has('portfolio')}
          aria-controls="mobile-kol-portfolio"
        >
          <ImageIcon className="h-5 w-5 shrink-0 text-accentText" />
          <span className="min-w-0 flex-1 text-sm font-semibold text-glass-text">Portfolio</span>
          <span className="text-xs text-glass-text-3">
            {(kol.portfolioItems || []).length} {(kol.portfolioItems || []).length === 1 ? 'item' : 'items'}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-glass-text-3 transition-transform ${
              expandedMobileSections.has('portfolio') ? 'rotate-180' : ''
            }`}
          />
        </button>
        <CardContent
          id="mobile-kol-portfolio"
          className={`${expandedMobileSections.has('portfolio') ? 'block' : 'hidden'} space-y-4 p-4 pt-0 md:block md:p-6 md:pt-0`}
        >
          <FileUploader
            accept="image/*,video/*"
            maxSize={50}
            onUpload={async (urls) => {
              for (const url of urls) {
                const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(url);
                await api.post(`/kols/${id}/portfolio`, {
                  type: isVideo ? 'VIDEO' : 'IMAGE',
                  url,
                  title: '',
                });
              }
              await loadKOL();
            }}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {(kol.portfolioItems || []).map((item: any) => (
              <div
                key={item.id}
                className="relative group rounded-xl overflow-hidden aspect-square bg-glass-3 border border-glass"
              >
                {item.type === 'VIDEO' ? (
                  <video src={item.url} className="w-full h-full object-cover" controls muted />
                ) : (
                  <img src={item.url} alt={item.title || ''} className="w-full h-full object-cover" />
                )}
                <button
                  type="button"
                  className="absolute right-1 top-1 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-glass-4 opacity-100 backdrop-blur-md transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/50 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
                  onClick={async () => {
                    await api.delete(`/kols/${id}/portfolio/${item.id}`);
                    loadKOL();
                  }}
                  aria-label={`Delete ${item.title || 'portfolio item'}`}
                >
                  <Trash2 className="w-4 h-4 text-rose-500 dark:text-rose-300" />
                </button>
              </div>
            ))}
          </div>
          {(!kol.portfolioItems || kol.portfolioItems.length === 0) && (
            <p className="text-sm text-glass-text-3">No portfolio items yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="hidden md:flex">
          <CardTitle>Rate card</CardTitle>
        </CardHeader>
        <button
          type="button"
          onClick={() => toggleMobileSection('rates')}
          className="flex min-h-14 w-full items-center gap-3 px-4 text-left transition-colors hover:bg-glass-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50 md:hidden"
          aria-expanded={expandedMobileSections.has('rates')}
          aria-controls="mobile-kol-rates"
        >
          <GitCompare className="h-5 w-5 shrink-0 text-accentText" />
          <span className="min-w-0 flex-1 text-sm font-semibold text-glass-text">Rate card</span>
          <span className="text-xs text-glass-text-3">{mobileRateSummary}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-glass-text-3 transition-transform ${
              expandedMobileSections.has('rates') ? 'rotate-180' : ''
            }`}
          />
        </button>
        <CardContent
          id="mobile-kol-rates"
          className={`${expandedMobileSections.has('rates') ? 'block' : 'hidden'} space-y-4 p-4 pt-0 md:block md:p-6 md:pt-0`}
        >
          <RateCardManager
            kolId={id}
            rateCards={rateCards}
            onReload={loadKOL}
          />

          <div className="border-t border-glass pt-4 mt-4">
            <h4 className="text-sm font-semibold text-glass-text flex items-center gap-2 mb-2">
              <GitCompare className="w-4 h-4 text-accent2" />
              Compare with another KOL
            </h4>
            <select
              className="glass-input w-full max-w-md px-3 py-2 text-sm mb-3"
              value={compareKolId}
              onChange={(e) => setCompareKolId(e.target.value)}
            >
              <option value="">Select KOL…</option>
              {rosterKols.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
            {compareKol && (
              <div className="overflow-x-auto text-sm rounded-xl border border-glass">
                <table className="w-full">
                  <thead>
                    <tr className="bg-glass-3 text-glass-text-2 uppercase tracking-[0.05em] text-xs">
                      <th className="p-2 text-left font-medium">Platform</th>
                      <th className="p-2 text-left font-medium">{kol.name} (post)</th>
                      <th className="p-2 text-left font-medium">{compareKol.name} (post)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(
                      new Set([
                        ...(kol.rateCards || []).map((r: any) => normalizeRatePlatform(r.platform)),
                        ...((compareKol as any).rateCards || []).map((r: any) => normalizeRatePlatform(r.platform)),
                      ])
                    ).map((plat) => {
                      const a = (kol.rateCards || []).find((r: any) => normalizeRatePlatform(r.platform) === plat);
                      const b = ((compareKol as any).rateCards || []).find((r: any) => normalizeRatePlatform(r.platform) === plat);
                      return (
                        <tr key={plat} className="border-t border-glass text-glass-text">
                          <td className="p-2 font-medium capitalize">{plat.replace('-', ' ')}</td>
                          <td className="p-2">RM{a?.postRate ?? '—'}</td>
                          <td className="p-2">RM{b?.postRate ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {availability.length > 0 && (
        <Card>
          <CardHeader className="hidden md:flex">
            <CardTitle>Availability</CardTitle>
          </CardHeader>
          <button
            type="button"
            onClick={() => toggleMobileSection('availability')}
            className="flex min-h-14 w-full items-center gap-3 px-4 text-left transition-colors hover:bg-glass-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50 md:hidden"
            aria-expanded={expandedMobileSections.has('availability')}
            aria-controls="mobile-kol-availability"
          >
            <MapPin className="h-5 w-5 shrink-0 text-accentText" />
            <span className="min-w-0 flex-1 text-sm font-semibold text-glass-text">Availability</span>
            <span className="text-xs text-glass-text-3">
              {availability.length} {availability.length === 1 ? 'window' : 'windows'}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-glass-text-3 transition-transform ${
                expandedMobileSections.has('availability') ? 'rotate-180' : ''
              }`}
            />
          </button>
          <CardContent
            id="mobile-kol-availability"
            className={`${expandedMobileSections.has('availability') ? 'block' : 'hidden'} p-4 pt-0 md:block md:p-6 md:pt-0`}
          >
            <ul className="text-sm space-y-1">
              {availability.map((a: any) => (
                <li key={a.id}>
                  {a.startDate?.slice?.(0, 10)} → {a.endDate?.slice?.(0, 10)}
                  {a.label ? ` · ${a.label}` : ''}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
